import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { PHONE_DISPLAY, PHONE_TEL, ADDRESS } from "@/lib/site";

const Section = ({ title, children }) => (
  <div className="mt-10">
    <h2 className="font-heading text-2xl text-forest">{title}</h2>
    <div className="mt-3 space-y-3 text-sm leading-relaxed text-charcoal/70 md:text-base">
      {children}
    </div>
  </div>
);

const PRIVACY = [
  {
    title: "What we collect",
    body: [
      "When you submit the enquiry form or contact us on WhatsApp, we may collect your name, mobile/WhatsApp number, email address, city and the nature of your interest in MAHIRA products.",
      "Our admin area uses a secure sign-in cookie solely to protect access to enquiry data.",
    ],
  },
  {
    title: "How we use it",
    body: [
      "Your details are used only to respond to your enquiry, share product and pricing information, and — where you have asked — keep you updated about new MAHIRA products.",
      "We do not sell, rent or share your personal information with third parties for their marketing.",
    ],
  },
  {
    title: "Your choices",
    body: [
      `You may ask us to update or delete your details at any time by calling ${PHONE_DISPLAY} or messaging us on WhatsApp.`,
    ],
  },
];

const TERMS = [
  {
    title: "Orders & pricing",
    body: [
      "Orders for MAHIRA A2 Gir Cow Ghee are placed directly via WhatsApp or phone call. Prices, sizes and availability are confirmed personally at the time of enquiry.",
      "An order is confirmed only once availability, price and delivery details are agreed on WhatsApp or call.",
    ],
  },
  {
    title: "Delivery",
    body: [
      "We currently serve Navi Mumbai and the wider Mumbai region, and are expanding across India. Delivery timelines and charges are confirmed per order.",
    ],
  },
  {
    title: "Product information",
    body: [
      "Content on this website describes quality, tradition, taste and culinary use. It is not medical advice, and MAHIRA makes no claims regarding disease prevention, treatment or guaranteed health outcomes.",
    ],
  },
  {
    title: "General",
    body: [
      `MAHIRA Agro Products operates from ${ADDRESS}. These terms are governed by the laws of India, with jurisdiction in Maharashtra.`,
    ],
  },
];

export default function Legal({ type = "privacy" }) {
  const isPrivacy = type === "privacy";
  const blocks = isPrivacy ? PRIVACY : TERMS;
  return (
    <div className="min-h-screen bg-ivory font-body text-charcoal">
      <header className="border-b border-forest/10 bg-ivory/90 backdrop-blur-xl">
        <div className="mx-auto flex h-[72px] max-w-4xl items-center justify-between px-5 md:px-8">
          <Link to="/" data-testid="legal-logo" className="flex flex-col leading-none">
            <span className="font-heading text-2xl font-semibold tracking-[0.18em] text-forest">
              MAHIRA
            </span>
            <span className="mt-1 text-[9px] font-semibold uppercase tracking-[0.42em] text-gold">
              Agro Products
            </span>
          </Link>
          <Link
            to="/"
            data-testid="legal-back-link"
            className="inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-forest/70 transition-colors duration-300 hover:text-forest"
          >
            <ArrowLeft className="h-4 w-4" /> Back to site
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-4xl px-5 py-16 md:px-8 md:py-24">
        <p className="text-xs font-semibold uppercase tracking-[0.35em] text-gold">
          Last updated · August 2026
        </p>
        <h1
          data-testid="legal-title"
          className="mt-4 font-heading text-4xl text-forest sm:text-5xl"
        >
          {isPrivacy ? "Privacy Policy" : "Terms & Conditions"}
        </h1>
        {blocks.map((b) => (
          <Section key={b.title} title={b.title}>
            {b.body.map((p) => (
              <p key={p.slice(0, 32)}>{p}</p>
            ))}
          </Section>
        ))}
        <div className="mt-14 rounded-3xl border border-forest/10 bg-cream p-7">
          <p className="text-sm text-charcoal/70">
            Questions? Call{" "}
            <a
              data-testid="legal-phone-link"
              href={PHONE_TEL}
              className="font-semibold text-forest underline decoration-gold underline-offset-4"
            >
              {PHONE_DISPLAY}
            </a>{" "}
            or message us on WhatsApp.
          </p>
        </div>
      </main>
    </div>
  );
}
